// Libs
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import moment from 'moment';
// Others
import { RootState } from '~/utils/redux/store';
import { DateFormatEnum } from '~/utils/enum/common';
import {
  createCustomer,
  editCustomer,
  deleteCustomer,
  exportCustomer,
  getCustomerDetail,
  getCustomerList,
  getScheduleCustomerList,
  importCustomer,
  sendToQuickBooks,
} from '~/thunks/customer/customerThunk';
import { ISchedule, IScheduleTimeLine } from '~/utils/interface/schedule';
import { CalendarViewMode, ScheduleMode } from '~/utils/type/common';
import { DEFAULT_VIEW_MODE, SCHEDULE_MODE_MY } from '~/utils/constants/common';

export interface ICustomerState {
  isRefreshCustomerList: boolean;
  scheduleCurrentTime: string;
  isRefreshScheduleCustomer: boolean;
  scheduleCustomer: IScheduleTimeLine[];
  scheduleMode: ScheduleMode;
  calendarViewMode: CalendarViewMode;
  events: ISchedule[];
}

export const initialState: ICustomerState = {
  isRefreshCustomerList: false,
  scheduleCurrentTime: moment().format(DateFormatEnum.YYYY_MM_DD),
  isRefreshScheduleCustomer: false,
  scheduleCustomer: [],
  scheduleMode: SCHEDULE_MODE_MY,
  calendarViewMode: DEFAULT_VIEW_MODE,
  events: [],
};

const customerSlice = createSlice({
  name: 'customer',
  initialState,
  reducers: {
    setRefreshCustomerList(state: ICustomerState, action: PayloadAction<boolean>) {
      state.isRefreshCustomerList = action.payload;
    },
    setScheduleCurrentTime(state: ICustomerState, action: PayloadAction<string>) {
      state.scheduleCurrentTime = action.payload;
    },
    setIsRefreshScheduleData(state: ICustomerState, action: PayloadAction<boolean>) {
      state.isRefreshScheduleCustomer = action.payload;
    },
    changeScheduleCustomer(state: ICustomerState, action: PayloadAction<IScheduleTimeLine[]>) {
      state.scheduleCustomer = action.payload;
    },
    changeEvents(state, action: PayloadAction<ISchedule[]>) {
      state.events = action.payload;
    },
    changeScheduleMode(state, action: PayloadAction<ScheduleMode>) {
      state.scheduleMode = action.payload;
    },
    changeCalendarViewMode(state, action: PayloadAction<CalendarViewMode>) {
      state.scheduleCurrentTime = moment().format(DateFormatEnum.YYYY_MM_DD);
      state.calendarViewMode = action.payload;
    },
  },
  extraReducers(builder) {
    // GET: Get customer list
    builder
      .addCase(getCustomerList.pending, (_state) => {})
      .addCase(getCustomerList.fulfilled, (_state) => {})
      .addCase(getCustomerList.rejected, (_state) => {});

    // POST: Create customer
    builder
      .addCase(createCustomer.pending, (_state) => {})
      .addCase(createCustomer.fulfilled, (_state) => {})
      .addCase(createCustomer.rejected, (_state) => {});

    // PUT: Edit customer
    builder
      .addCase(editCustomer.pending, (_state) => {})
      .addCase(editCustomer.fulfilled, (_state) => {})
      .addCase(editCustomer.rejected, (_state) => {});

    // GET: Get customer detail
    builder
      .addCase(getCustomerDetail.pending, (_state) => {})
      .addCase(getCustomerDetail.fulfilled, (_state) => {})
      .addCase(getCustomerDetail.rejected, (_state) => {});

    // GET: Get schedule customer list
    builder
      .addCase(getScheduleCustomerList.pending, (_state) => {})
      .addCase(getScheduleCustomerList.fulfilled, (state, action) => {
        if (state.calendarViewMode === 'month') {
          state.events = action.payload.data as ISchedule[];
        } else if (state.calendarViewMode === 'week' || state.calendarViewMode === 'day') {
          state.scheduleCustomer = action.payload.data as IScheduleTimeLine[];
        }
        state.isRefreshScheduleCustomer = false;
      })
      .addCase(getScheduleCustomerList.rejected, (_state) => {});

    // POST: Import customer
    builder
      .addCase(importCustomer.pending, (_state) => {})
      .addCase(importCustomer.fulfilled, (_state) => {})
      .addCase(importCustomer.rejected, (_state) => {});

    // GET: Export customer
    builder
      .addCase(exportCustomer.pending, (_state) => {})
      .addCase(exportCustomer.fulfilled, (_state) => {})
      .addCase(exportCustomer.rejected, (_state) => {});

    // PUT: Send to QuickBooks
    builder
      .addCase(sendToQuickBooks.pending, (_state) => {})
      .addCase(sendToQuickBooks.fulfilled, (_state) => {})
      .addCase(sendToQuickBooks.rejected, (_state) => {});

    // DELETE: Delete customer
    builder
      .addCase(deleteCustomer.pending, (state) => {})
      .addCase(deleteCustomer.fulfilled, (state) => {})
      .addCase(deleteCustomer.rejected, (state) => {});
  },
});

// Action
export const customerActions = customerSlice.actions;

// Selectors
export const selectIsRefreshCustomerList = (state: RootState) => state.customer.isRefreshCustomerList;

// Reducer
const customerReducer = customerSlice.reducer;
export default customerReducer;
